import React from "react";
import { action, computed, observable, makeObservable, runInAction } from "mobx";
import { MrbBaseEditViewStore } from "mrb/core";
import { MrbFieldSelectStore } from "mrb/components/select";
import { MrbSelectStore } from "mrb/components/select/stores";
import { NakiNigunAlbumEditForm } from "administration/naki-nigun/forms";
import { NakiNigunAlbumCoverImageUploadViewStore } from "administration/naki-nigun/stores";
import { find, findIndex, isNil, map, sumBy, some, uniqBy } from "lodash";

class ProducerRow {
    @observable producerId = null;
    @observable revenuePercentage = 0;
    @observable isActive = true;
    producerSelectStore = null;

    constructor(findProducers, existingProducer = null) {
        makeObservable(this);

        this.producerSelectStore = new MrbSelectStore({
            isSearchable: true,
            virtualized: false,
            initFetch: false,
            textKey: "name",
            valueKey: "id",
            isClearable: true,
            actions: {
                find: (searchTerm) => findProducers(searchTerm),
                onChange: action((selectedOption) => {
                    this.producerId = selectedOption ? selectedOption.value : null;
                }),
            },
        });

        if (existingProducer) {
            this.producerId = existingProducer.producerId;
            this.revenuePercentage = existingProducer.revenuePercentage;
            this.isActive = existingProducer.isActive;
            if (existingProducer.producer) {
                this.producerSelectStore.setSelectedItem(existingProducer.producer);
            }
        }
    }
}

class NakiNigunAlbumEditViewStore extends MrbBaseEditViewStore {
    @observable overrideRadioUsersSettingsIsVisible = false;
    @observable overrideAppUsersSettingsIsVisible = false;
    @observable isCollapsed = false;
    @observable producerRows = [];
    panelRef = React.createRef();

    @computed get totalRevenue() {
        return sumBy(this.producerRows, (row) => row.revenuePercentage);
    }

    @computed get producersAreValid() {
        if (this.producerRows.length === 0) {
            return true;
        }
        if (some(this.producerRows, (row) => isNil(row.producerId))) {
            return false;
        }
        if (some(this.producerRows, (row) => row.revenuePercentage < 0.01)) {
            return false;
        }
        const uniqueProducers = uniqBy(this.producerRows, (row) => row.producerId);
        if (uniqueProducers.length !== this.producerRows.length) {
            return false;
        }
        return Math.round(this.totalRevenue * 100) / 100 === 100;
    }

    @computed get album() {
        return find(this.nakiNigunArtistEditViewStore.albums, (album) => album.id === this.albumId);
    }

    @computed get isNewlyAdded() {
        return isNil(this.album.dateCreated);
    }

    @computed get albumIndex() {
        return findIndex(this.nakiNigunArtistEditViewStore.albums, (album) => album.id === this.albumId) + 1;
    }

    @computed get episodeSortTypeOptions() {
        return this.nakiNigunArtistEditViewStore.episodeSortTypeOptions;
    }

    @computed get disabledForShufflingWeight() {
        return this.nakiNigunArtistEditViewStore.disabledForShufflingWeight;
    }

    @computed get isPremiumDisabled() {
        return this.nakiNigunArtistEditViewStore.form.$("isPremium").value;
    }

    @computed get isActiveStatusDisabled() {
        return !this.nakiNigunArtistEditViewStore.form.$("isActive").value;
    }

    @computed get showScheduledRelease() {
        return !this.form.$("isActive").value;
    }

    constructor(rootStore, { nakiNigunArtistEditViewStore, albumId }) {
        super(rootStore, {
            name: "naki-nigun-albums",
            FormClass: NakiNigunAlbumEditForm,
            redirectOnCreateSuccess: false,
        });
        makeObservable(this);
        this.albumId = albumId;
        this.nakiNigunArtistEditViewStore = nakiNigunArtistEditViewStore;
        this.routeStore = this.nakiNigunArtistEditViewStore.routeStore;

        this.coverImageUploadViewStore = new NakiNigunAlbumCoverImageUploadViewStore(this.rootStore, {
            routeStore: this.routeStore,
            width: 200,
            height: 200,
        });

        this.genreSelectStore = new MrbFieldSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                isMulti: true,
                initFetch: false,
                actions: {
                    find: (searchTerm) => {
                        return this.routeStore.findGenres(searchTerm);
                    },
                    createOption: (name) => {
                        return this.routeStore.createGenre({ name });
                    },
                },
            },
            this.form.$("genreIds")
        );

        this.findProducers = this.routeStore.findProducers.bind(this.routeStore);

        if (!this.isNewlyAdded) {
            this.reaction(
                () => this.form.$("isTurnedOnByDefault").value,
                (isTurnedOnByDefault) => {
                    if (isTurnedOnByDefault !== this.album.isTurnedOnByDefault) {
                        runInAction(() => {
                            this.overrideRadioUsersSettingsIsVisible = true;
                        });
                    } else {
                        runInAction(() => {
                            this.overrideRadioUsersSettingsIsVisible = false;
                            this.form.$("overrideRadioUsersSettings").value = false;
                        });
                    }
                }
            );
            this.reaction(
                () => this.form.$("isTurnedOnByDefaultInApp").value,
                (isTurnedOnByDefault) => {
                    if (isTurnedOnByDefault !== this.album.isTurnedOnByDefaultInApp) {
                        runInAction(() => {
                            this.overrideAppUsersSettingsIsVisible = true;
                        });
                    } else {
                        runInAction(() => {
                            this.overrideAppUsersSettingsIsVisible = false;
                            this.form.$("overrideAppUsersSettings").value = false;
                        });
                    }
                }
            );
        }

        this.setForm();

        this.reaction(
            () => this.isCollapsed,
            (isCollapsed) => {
                if (isCollapsed) {
                    this.form.$("title").focus();
                    if (this.panelRef) {
                        // We need to execute scroll function asynchronously in order to take effect.
                        setTimeout(() => {
                            this.panelRef.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                            });
                        });
                    }
                }
            }
        );

        this.reaction(
            () => this.form.changed,
            (isChanged) => {
                if (isChanged) {
                    this.nakiNigunArtistEditViewStore.markAlbumAsChanged(this.albumId);
                }
            }
        );

        this.reaction(
            () => this.coverImageUploadViewStore.hasChanged,
            (isChanged) => {
                if (isChanged) {
                    this.nakiNigunArtistEditViewStore.markAlbumAsChanged(this.albumId);
                }
            }
        );

        this.reaction(
            () => this.nakiNigunArtistEditViewStore.form.$("isPremium").value,
            (isArtistPremium) => {
                if (isArtistPremium) {
                    this.form.$("isPremium").onChange(isArtistPremium);
                }
            }
        );

        this.reaction(
            () => this.nakiNigunArtistEditViewStore.form.$("isActive").value,
            (isActive) => {
                this.form.$("isActive").onChange(isActive);
            }
        );
    }

    @action.bound
    setForm() {
        this.form.set({
            ...this.album,
            genreIds: this.album.genres ? map(this.album.genres, (genre) => genre.id) : null,
        });
        this.form.$("scheduledReleaseAt").set("value", this.album.scheduledReleaseAt || "");
        this.genreSelectStore.setSelectedItem(this.album.genres || null);
        if (this.album.producers && this.album.producers.length > 0) {
            this.producerRows = this.album.producers.map((p) => new ProducerRow(this.findProducers, p));
        } else {
            this.producerRows = [];
        }
        if (this.album.coverImageId) {
            if (this.isNewlyAdded) {
                this.coverImageUploadViewStore.setUploadedFileId(this.album.coverImageId);
            } else {
                this.coverImageUploadViewStore.setImageUrl(
                    this.routeStore.getNakiNigunAlbumCoverImageUrl(this.album.coverImageId)
                );
            }
        } else {
            this.coverImageUploadViewStore.setUploadedFileId(null);
        }
    }

    resetForm() {
        this.form.clear();
        this.setForm();
        this.nakiNigunArtistEditViewStore.markAlbumAsUpdated(this.album.id);
    }

    @action.bound
    onInit() {
        const albumId = this.rootStore.routerStore.routerState.queryParams.albumId;
        if (albumId && this.albumId === albumId) {
            this.isCollapsed = true;
        }
        return Promise.resolve();
    }

    async create(data) {
        if (!this.producersAreValid) {
            return;
        }
        if (this.coverImageUploadViewStore.hasChanged) {
            data.coverImageId = this.coverImageUploadViewStore.uploadedFileId;
        } else {
            data.coverImageId = this.album.coverImageId;
        }
        const selectedGenres = this.genreSelectStore.selectedOption;
        if (selectedGenres) {
            data.genres = map(selectedGenres, (genreOption) => genreOption.item);
        }
        data.producers = map(this.producerRows, (row) => ({
            isActive: row.isActive,
            producerId: row.producerId,
            producer: row.producerSelectStore.selectedItem,
            revenuePercentage: row.revenuePercentage,
        }));

        const scheduledReleaseAt = this.form.$("scheduledReleaseAt").value || null;
        data.scheduledReleaseAt = data.isActive ? null : (scheduledReleaseAt ? new Date(scheduledReleaseAt).toISOString() : null);

        this.nakiNigunArtistEditViewStore.updateAlbum(this.albumId, data);
        this.form.$changed = false;
        this.form.each((field) => (field.$changed = false));
    }

    onCreateError(err) {
        this.rootStore.notificationStore.error("An unexpected error occurred while trying to update resource.", err);
    }

    @action.bound
    addProducerRow() {
        this.producerRows.push(new ProducerRow(this.findProducers));
        this.nakiNigunArtistEditViewStore.markAlbumAsChanged(this.albumId);
    }

    @action.bound
    removeProducerRow(index) {
        this.producerRows.splice(index, 1);
        this.nakiNigunArtistEditViewStore.markAlbumAsChanged(this.albumId);
    }

    @action.bound
    setProducerRowRevenue(index, value) {
        const parsed = parseFloat(value);
        this.producerRows[index].revenuePercentage = isNaN(parsed)
            ? 0
            : Math.round(Math.max(0, Math.min(100, parsed)) * 100) / 100;
        this.nakiNigunArtistEditViewStore.markAlbumAsChanged(this.albumId);
    }

    setPanelRef = (ref) => {
        this.panelRef = ref;
    };

    @action.bound
    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
    }

    deleteAlbum = (event) => {
        // Prevent collapsing the album panel when a user clicks on the delete button.
        event.stopPropagation();
        event.preventDefault();
        this.nakiNigunArtistEditViewStore.deleteAlbum(this.albumId);
    };
}

export default NakiNigunAlbumEditViewStore;
